import * as ast from "./ast.js";
import { lex, lexRecover, LexError } from "./lexer.js";
import { Span, Token, TokenType } from "./token.js";

export class ParseError extends Error {
  constructor(message: string, public span: Span) {
    super(message);
    this.name = "ParseError";
  }
}

/** Strict parse: the first lex/parse error throws. Used by the CLI and runner. */
export function parse(src: string): ast.Program {
  return new Parser(lex(src), false).parseProgram();
}

export interface RecoverResult {
  program: ast.Program;
  errors: (ParseError | LexError)[];
}

/** Tolerant parse: never throws. Malformed statements/declarations are dropped
 *  (with errors collected) but everything that parses is kept, so editor
 *  features work on an in-progress edit. */
export function parseRecover(src: string): RecoverResult {
  const { tokens, errors: lexErrors } = lexRecover(src);
  const parser = new Parser(tokens, true);
  const program = parser.parseProgram();
  return { program, errors: [...lexErrors, ...parser.errors] };
}

const ASSIGN_OPS: TokenType[] = ["=", "+=", "-=", "*=", "/="];

/** Tokens that plausibly begin a statement — synchronization points after a
 *  parse error, so recovery resumes at the next statement rather than mid-noise. */
const STMT_START = new Set<TokenType>([
  "let", "return", "if", "while", "for", "match", "go", "txn", "retry",
  "raise", "try", "fn", "struct", "enum", "trait", "impl", "{",
]);

class Parser {
  private pos = 0;
  readonly errors: ParseError[] = [];
  /** Whether a `{` in expression position opens a *block* rather than a set or dict
   *  literal. Set while parsing the header expression of an `if`/`while`/`for`/
   *  `match`, where a block follows the expression and the two spellings collide
   *  (`for x in {1: 2} { … }`). Rust resolves the same ambiguity with the same
   *  restriction: parenthesize the literal there. Any bracketed group inside the
   *  header clears it again (`if xs.has({1: 2})` is unambiguous), which is what
   *  `parseBracketed` is for. */
  private noBraceLiteral = false;
  constructor(
    private tokens: Token[],
    private recover: boolean
  ) {}

  /** Parse a control-flow header expression — the one a `{` block follows — with
   *  brace literals disabled inside it (see `noBraceLiteral`). */
  private parseHeaderExpr(): ast.Expr {
    const saved = this.noBraceLiteral;
    this.noBraceLiteral = true;
    try {
      return this.parseExpr();
    } finally {
      this.noBraceLiteral = saved;
    }
  }

  /** Parse the inside of a bracketed group — `(…)`, `[…]`, `{…}`, an argument list —
   *  where a `{` is unambiguously a literal again, because a block can't appear
   *  there. Restores the enclosing setting afterwards. */
  private parseBracketed<T>(body: () => T): T {
    const saved = this.noBraceLiteral;
    this.noBraceLiteral = false;
    try {
      return body();
    } finally {
      this.noBraceLiteral = saved;
    }
  }

  // -- token helpers ------------------------------------------------------

  private peek(ahead = 0): Token {
    return this.tokens[Math.min(this.pos + ahead, this.tokens.length - 1)]!;
  }

  private at(type: TokenType): boolean {
    return this.peek().type === type;
  }

  private next(): Token {
    const t = this.tokens[this.pos]!;
    if (this.pos < this.tokens.length - 1) this.pos++;
    return t;
  }

  private expect(type: TokenType): Token {
    if (!this.at(type)) {
      const t = this.peek();
      throw new ParseError(
        `expected ${type} but found ${t.type === "eof" ? "end of input" : JSON.stringify(t.text)}`,
        t.span
      );
    }
    return this.next();
  }

  private accept(type: TokenType): Token | null {
    return this.at(type) ? this.next() : null;
  }

  private span(startTok: Token, endTok: Token = this.tokens[this.pos - 1] ?? startTok): Span {
    return { start: startTok.span.start, end: endTok.span.end };
  }

  // -- program ------------------------------------------------------------

  parseProgram(): ast.Program {
    const start = this.peek();
    const decls: ast.Decl[] = [];
    while (!this.at("eof")) {
      const before = this.pos;
      try {
        decls.push(this.parseDecl());
      } catch (e) {
        if (!this.recover || !(e instanceof ParseError)) throw e;
        this.errors.push(e);
        this.syncTo(false);
      }
      if (this.pos === before) this.next(); // guarantee forward progress
    }
    return { decls, span: this.span(start) };
  }

  /** Skip tokens until a likely resumption point after a parse error. When
   *  `inBlock`, stop at `}`, a statement keyword, or an identifier (expression
   *  statements and assignments begin with one, and this language has no
   *  statement separators); otherwise only at declaration starters. */
  private syncTo(inBlock: boolean): void {
    while (!this.at("eof")) {
      const t = this.peek().type;
      if (t === ";") {
        this.next();
        return;
      }
      if (inBlock) {
        if (t === "}" || t === "ident" || STMT_START.has(t)) return;
      } else if (t === "fn" || t === "struct" || t === "enum" || t === "trait" || t === "impl") {
        return;
      }
      this.next();
    }
  }

  private parseDecl(): ast.Decl {
    if (this.at("struct")) return this.parseStruct();
    // `builtin struct Name<...>` — a natively-backed type declared (headers only, no
    // fields) so its name has a source location. `builtin` is a contextual keyword
    // (like before `fn`), triggering only when immediately followed by `struct`.
    if (this.at("ident") && this.peek().text === "builtin" && this.peek(1).type === "struct") {
      this.next(); // consume `builtin`
      return this.parseStruct(true);
    }
    if (this.at("enum")) return this.parseEnum();
    if (this.at("trait")) return this.parseTrait();
    if (this.at("impl")) return this.parseImpl();
    // Effects are declared with a trailing `! io retry` annotation (parsed in
    // `parseFn`/`parseEffects`), not a leading modifier.
    if (this.at("fn")) return this.parseFn(false, true, false);
    // `builtin fn name(params) Ret` — a declaration whose implementation is
    // provided natively (see `FnDecl.builtin`). `builtin` is a contextual keyword
    // (like `key`), so it only triggers when immediately followed by `fn`.
    if (this.at("ident") && this.peek().text === "builtin" && this.peek(1).type === "fn") {
      this.next(); // consume `builtin`
      return this.parseFn(false, true, true);
    }
    const t = this.peek();
    throw new ParseError(
      `expected a top-level declaration (struct, enum, trait, impl, or fn) but found ${JSON.stringify(t.text)}`,
      t.span
    );
  }

  private parseStruct(builtin = false): ast.StructDecl {
    const start = this.expect("struct");
    const nameTok = this.expect("ident");
    const typeParams = this.at("<") ? this.parseFnTypeParams() : [];
    // A `builtin struct` has no fields; its body braces may be omitted entirely
    // (`builtin struct Dict<K, V>`). An ordinary struct still requires a `{ … }`.
    let fields: ast.FieldDecl[] = [];
    if (!builtin || this.at("{")) {
      this.expect("{");
      fields = this.parseFieldList("}");
      this.expect("}");
    }
    return { kind: "struct", name: nameTok.text, nameSpan: nameTok.span, typeParams, fields, builtin: builtin || undefined, span: this.span(start) };
  }

  private parseFieldList(terminator: TokenType): ast.FieldDecl[] {
    const fields: ast.FieldDecl[] = [];
    while (!this.at(terminator) && !this.at("eof")) {
      const fstart = this.peek();
      const fname = this.expect("ident");
      this.accept(":"); // colon between name and type is optional
      const type = this.parseType();
      // Optional `key` modifier: marks a struct field as a table's primary key.
      // Contextual (not a reserved word), so only consumed when it reads `key`.
      const key = this.at("ident") && this.peek().text === "key" ? (this.next(), true) : false;
      // An optional default value (`x int = expr`), used when the field is omitted.
      const def = this.accept("=") ? this.parseExpr() : undefined;
      this.accept(","); // commas optional (also newline-separated)
      fields.push({ name: fname.text, nameSpan: fname.span, type, key, default: def, span: this.span(fstart) });
    }
    return fields;
  }

  private parseEnum(): ast.EnumDecl {
    const start = this.expect("enum");
    const nameTok = this.expect("ident");
    const typeParams = this.at("<") ? this.parseFnTypeParams() : [];
    this.expect("{");
    const variants: ast.EnumVariant[] = [];
    while (!this.at("}") && !this.at("eof")) {
      const vstart = this.peek();
      const vname = this.expect("ident");
      let fields: ast.FieldDecl[] = [];
      if (this.accept("{")) {
        fields = this.parseFieldList("}");
        this.expect("}");
      } else if (this.accept("(")) {
        fields = this.parseFieldList(")");
        this.expect(")");
      }
      this.accept(",");
      variants.push({ name: vname.text, nameSpan: vname.span, fields, span: this.span(vstart) });
    }
    this.expect("}");
    return { kind: "enum", name: nameTok.text, nameSpan: nameTok.span, typeParams, variants, span: this.span(start) };
  }

  /** Parse a trailing effect annotation — `!` followed by one or more effect names
   *  from the closed vocabulary (`io`, `retry`, `raises`, `spawn`), placed after the
   *  return type (or, for a void return, after the parameter list): `open(path) Res<…>
   *  ! io`, `orElse(a, b) T ! retry`, `fetchJson(c, url) JSONValue ! raises`,
   *  `serve(ln) ! spawn`. An absent `!` is the empty (pure) set; a `!` with no effect
   *  name after it is an error. The names are contextual here — ordinary identifiers
   *  elsewhere — so the run ends at the first token that is not one. */
  private parseEffects(): ast.EffectName[] {
    if (!this.accept("!")) return [];
    const out: ast.EffectName[] = [];
    // `io`, `raises` and `spawn` lex as plain identifiers (`spawn` is also the stdlib
    // function that wraps a `go` in a `Future`); `retry` is a keyword token (it also
    // heads a `retry` statement). All four are effect names here — contextual,
    // terminating the run at the first token that is none of them.
    const effectName = (): ast.EffectName | null => {
      const t = this.peek();
      if (t.type === "ident" && t.text === "io") return "io";
      if (t.type === "ident" && t.text === "raises") return "raises";
      if (t.type === "ident" && t.text === "spawn") return "spawn";
      if (t.type === "retry") return "retry";
      return null;
    };
    do {
      const name = effectName();
      if (name === null) {
        const t = this.peek();
        throw new ParseError(
          `unknown effect \`${t.text}\` (expected \`io\`, \`retry\`, \`raises\`, or \`spawn\`)`,
          t.span
        );
      }
      this.next();
      if (!out.includes(name)) out.push(name);
    } while (effectName() !== null);
    return out;
  }

  private parseFn(allowSelf = false, allowTypeParams = false, builtin = false): ast.FnDecl {
    const start = this.expect("fn");
    const nameTok = this.expect("ident");
    const typeParams = allowTypeParams && this.at("<") ? this.parseFnTypeParams() : [];
    // A `builtin` declaration may leave a parameter untyped (`min(a, b)`) since it
    // is never checked structurally — its native implementation defines the shape.
    const { self, params } = this.parseParams(allowSelf, builtin);
    let returnType: ast.TypeExpr | null = null;
    if (builtin) {
      // A builtin has no `{` body to bound the (optional) return type, so use the
      // source line: a type on the *same* line as the closing `)` is the return
      // type (`str(value) string`); otherwise there is none (`print(value)`), and
      // the next token begins the following declaration.
      const closeLine = this.tokens[this.pos - 1]!.span.end.line;
      if (this.peek().span.start.line === closeLine && (this.at("ident") || this.at("("))) {
        returnType = this.parseType();
      }
    } else if (!this.at("{") && !this.at("!")) {
      returnType = this.parseType();
    }
    // The effect annotation trails the signature, between the return type and the
    // body (`fn f(x) T ! io { … }`, `builtin fn open(p) Res<…> ! io`).
    const effects = this.parseEffects();
    // A `builtin fn` has no body — the implementation is plugged in natively. We
    // still give it an empty block so downstream code sees a uniform `FnDecl`.
    const body: ast.Block = builtin
      ? { kind: "block", stmts: [], span: this.span(nameTok) }
      : this.parseBlock();
    return {
      kind: "fn",
      name: nameTok.text,
      nameSpan: nameTok.span,
      typeParams,
      self,
      params,
      returnType,
      builtin: builtin || undefined,
      effects: effects.length ? effects : undefined,
      body,
      span: this.span(start),
    };
  }

  /** Parse `<T, U: Trait, V: A + B>` — generic type parameters with bounds. */
  private parseFnTypeParams(): ast.TypeParam[] {
    this.expect("<");
    const out: ast.TypeParam[] = [];
    while (!this.at(">") && !this.at("eof")) {
      const nameTok = this.expect("ident");
      const bounds: { name: string; span: Span }[] = [];
      if (this.accept(":")) {
        do {
          const b = this.expect("ident");
          bounds.push({ name: b.text, span: b.span });
        } while (this.accept("+"));
      }
      out.push({ name: nameTok.text, nameSpan: nameTok.span, bounds, span: this.span(nameTok) });
      if (!this.accept(",")) break;
    }
    this.expect(">");
    return out;
  }

  /** Parse a `(...)` parameter list. When `allowSelf`, a bare leading `self`
   *  (no type) is the method receiver — returned separately, not as a param. When
   *  `allowUntyped` (a `builtin` declaration), a parameter may omit its type
   *  (`min(a, b)`); the missing type is recorded as the placeholder `any`, since a
   *  builtin's argument shape is defined by its native implementation, not checked
   *  structurally here. */
  private parseParams(allowSelf: boolean, allowUntyped = false): { self?: Span; params: ast.Param[] } {
    this.expect("(");
    let self: Span | undefined;
    const params: ast.Param[] = [];
    let first = true;
    while (!this.at(")") && !this.at("eof")) {
      if (first && allowSelf && this.at("ident") && this.peek().text === "self") {
        self = this.next().span;
      } else {
        const pstart = this.peek();
        const pname = this.expect("ident");
        const hasColon = this.accept(":") !== null;
        // Untyped only when explicitly allowed and nothing follows the name — a
        // colon or a bare type token (`x float`) still means an annotated param.
        const type: ast.TypeExpr =
          !hasColon && allowUntyped && (this.at(",") || this.at(")"))
            ? { kind: "named", name: "any", args: [], span: pname.span }
            : this.parseType();
        // An optional default value (`y: int = expr`) makes the parameter omittable.
        const def = this.accept("=") ? this.parseExpr() : undefined;
        params.push({ name: pname.text, nameSpan: pname.span, type, default: def, span: this.span(pstart) });
      }
      first = false;
      if (!this.accept(",")) break;
    }
    this.expect(")");
    return { self, params };
  }

  private parseTrait(): ast.TraitDecl {
    const start = this.expect("trait");
    const nameTok = this.expect("ident");
    const typeParams = this.at("<") ? this.parseFnTypeParams() : [];
    this.expect("{");
    const methods: ast.TraitMethodSig[] = [];
    while (!this.at("}") && !this.at("eof")) {
      const mstart = this.expect("fn");
      const mname = this.expect("ident");
      // The method may declare its own generic type parameters, e.g. `map<U>`.
      const typeParams = this.at("<") ? this.parseFnTypeParams() : [];
      const { self, params } = this.parseParams(true);
      if (!self) {
        throw new ParseError(`trait method \`${mname.text}\` must take \`self\` as its first parameter`, mname.span);
      }
      // A return type runs until an effect annotation, a body, the next method
      // (`fn`), or the trait's closing brace. A body makes this a default impl.
      let returnType: ast.TypeExpr | null = null;
      if (!this.at("{") && !this.at("!") && !this.at("fn") && !this.at("}")) returnType = this.parseType();
      const effects = this.parseEffects();
      const body = this.at("{") ? this.parseBlock() : undefined;
      this.accept(",");
      methods.push({
        name: mname.text,
        nameSpan: mname.span,
        typeParams,
        self,
        params,
        returnType,
        effects: effects.length ? effects : undefined,
        body,
        span: this.span(mstart),
      });
    }
    this.expect("}");
    return { kind: "trait", name: nameTok.text, nameSpan: nameTok.span, typeParams, methods, span: this.span(start) };
  }

  private parseImpl(): ast.ImplDecl {
    const start = this.expect("impl");
    // Optional impl type parameters: `impl<T> ...`, `impl<K, V> ...`.
    const typeParams: string[] = [];
    if (this.at("<")) {
      this.next();
      while (!this.at(">") && !this.at("eof")) {
        typeParams.push(this.expect("ident").text);
        if (!this.accept(",")) break;
      }
      this.expect(">");
    }
    // Two forms:
    //   `impl Trait for Target { ... }`  — a trait implementation
    //   `impl Target { ... }`            — an *inherent* impl (no trait): methods
    //                                      attached directly to the target type.
    // Parse the first type; if a `for` follows, it was the trait and the target
    // comes next. Otherwise the first type *is* the target and there is no trait.
    const firstTok = this.peek();
    const firstType = this.parseType();
    if (firstType.kind !== "named") {
      throw new ParseError("expected a type name after `impl`", firstType.span);
    }
    let traitTok = firstTok;
    let traitType: ast.NamedType | null = firstType;
    let typeTok = firstTok;
    let target = firstType;
    if (this.accept("for")) {
      // `impl Trait for Target` — the first type was the trait.
      typeTok = this.peek();
      const targetType = this.parseType();
      if (targetType.kind !== "named") {
        throw new ParseError("expected a type name after `for`", targetType.span);
      }
      target = targetType;
    } else {
      // `impl Target` — inherent impl, no trait.
      traitType = null;
    }
    this.expect("{");
    const methods: ast.FnDecl[] = [];
    while (!this.at("}") && !this.at("eof")) {
      // `builtin fn name(self, …) Ret` — a method whose implementation is provided
      // natively (see `FnDecl.builtin`), the method analogue of a `builtin fn` free
      // function. `builtin` is a contextual keyword, treated as one only before `fn`.
      // Effects trail the signature (`! io`), parsed in `parseFn`.
      if (this.at("ident") && this.peek().text === "builtin" && this.peek(1).type === "fn") {
        this.next(); // consume `builtin`
        methods.push(this.parseFn(true, true, true));
        continue;
      }
      if (!this.at("fn")) {
        throw new ParseError(`expected a method (fn) in impl but found ${JSON.stringify(this.peek().text)}`, this.peek().span);
      }
      // Impl methods may be generic (their own `<U>`), e.g. to implement a
      // generic trait method.
      methods.push(this.parseFn(true, true, false));
    }
    this.expect("}");
    return {
      kind: "impl",
      name: target.name,
      typeParams,
      // An inherent impl has no trait; represented as an empty trait name.
      trait: traitType ? traitType.name : "",
      traitArgs: traitType ? traitType.args : [],
      traitSpan: traitTok.span,
      target,
      typeName: target.name,
      typeSpan: typeTok.span,
      methods,
      span: this.span(start),
    };
  }

  // -- types --------------------------------------------------------------

  private parseType(): ast.TypeExpr {
    // A function type: `(T, U) -> R`. The parenthesized list is the parameter
    // types; the type after `->` is the result. Right-associative, so
    // `(int) -> (int) -> int` reads as `(int) -> ((int) -> int)`.
    if (this.at("(")) {
      const start = this.expect("(");
      const params: ast.TypeExpr[] = [];
      while (!this.at(")") && !this.at("eof")) {
        params.push(this.parseType());
        if (!this.accept(",")) break;
      }
      this.expect(")");
      this.expect("->");
      const ret = this.parseType();
      return { kind: "fn", params, ret, span: this.span(start) };
    }
    const start = this.expect("ident");
    const args = this.at("<") ? this.parseTypeArgs() : [];
    return { kind: "named", name: start.text, args, span: this.span(start) };
  }

  /** Parse `<T, U, ...>` — the angle-bracketed type argument list. */
  private parseTypeArgs(): ast.TypeExpr[] {
    this.expect("<");
    const args: ast.TypeExpr[] = [];
    while (!this.at(">") && !this.at("eof")) {
      args.push(this.parseType());
      if (!this.accept(",")) break;
    }
    this.expect(">");
    return args;
  }

  // -- statements ---------------------------------------------------------

  private parseBlock(): ast.Block {
    const start = this.expect("{");
    const stmts: ast.Stmt[] = [];
    while (!this.at("}") && !this.at("eof")) {
      const before = this.pos;
      try {
        stmts.push(this.parseStmt());
        this.accept(";");
      } catch (e) {
        if (!this.recover || !(e instanceof ParseError)) throw e;
        this.errors.push(e);
        this.syncTo(true);
      }
      if (this.pos === before) this.next(); // guarantee forward progress
    }
    // A missing close brace is reported but does not abort recovery.
    if (this.at("}")) this.next();
    else if (this.recover) this.errors.push(new ParseError("expected } but found end of input", this.peek().span));
    else this.expect("}");
    return { kind: "block", stmts, span: this.span(start) };
  }

  private parseStmt(): ast.Stmt {
    const t = this.peek();
    switch (t.type) {
      case "let":
        return this.parseLet();
      case "return":
        return this.parseReturn();
      case "if":
        return this.parseIf();
      case "while":
        return this.parseWhile();
      case "for":
        return this.parseFor();
      case "match":
        return this.parseMatch();
      case "go":
        return this.parseGo();
      case "txn":
        return this.parseTxn();
      case "retry":
        return this.parseRetry();
      case "raise":
        return this.parseRaise();
      case "try":
        return this.parseTry();
      case "assert":
        return this.parseAssert();
      case "{":
        return this.parseBlock();
      default:
        return this.parseExprOrAssign();
    }
  }

  private parseLet(): ast.LetStmt {
    const start = this.expect("let");
    const nameTok = this.expect("ident");
    let type: ast.TypeExpr | null = null;
    if (this.accept(":")) type = this.parseType();
    this.expect("=");
    const value = this.parseExpr();
    return { kind: "let", name: nameTok.text, nameSpan: nameTok.span, type, value, span: this.span(start) };
  }

  private parseReturn(): ast.ReturnStmt {
    const start = this.expect("return");
    let value: ast.Expr | null = null;
    if (!this.at("}") && !this.at(";") && !this.at("eof")) {
      value = this.parseExpr();
    }
    return { kind: "return", value, span: this.span(start) };
  }

  private parseIf(): ast.IfStmt {
    const start = this.expect("if");
    const cond = this.parseHeaderExpr();
    const then = this.parseBlock();
    let elseBranch: ast.Block | ast.IfStmt | null = null;
    if (this.accept("else")) {
      elseBranch = this.at("if") ? this.parseIf() : this.parseBlock();
    }
    return { kind: "if", cond, then, else: elseBranch, span: this.span(start) };
  }

  private parseWhile(): ast.WhileStmt {
    const start = this.expect("while");
    const cond = this.parseHeaderExpr();
    const body = this.parseBlock();
    return { kind: "while", cond, body, span: this.span(start) };
  }

  private parseFor(): ast.ForStmt {
    const start = this.expect("for");
    const varTok = this.expect("ident");
    this.expect("in");
    const iterable = this.parseHeaderExpr();
    const body = this.parseBlock();
    return {
      kind: "for",
      varName: varTok.text,
      varSpan: varTok.span,
      iterable,
      body,
      span: this.span(start),
    };
  }

  private parseGo(): ast.GoStmt {
    const start = this.expect("go");
    const call = this.parseSpawnCall();
    return { kind: "go", call, span: this.span(start) };
  }

  /** The `f(...)` after a `go`. It is a *direct* function call — a bare name,
   *  optional explicit type arguments, then one argument list. A non-call or a
   *  method receiver (`go x.m()`) is rejected. */
  private parseSpawnCall(): ast.CallExpr {
    const nameTok = this.peek();
    if (nameTok.type !== "ident") {
      throw new ParseError("`go` must be followed by a direct function call", nameTok.span);
    }
    this.next();
    const callee: ast.Expr = { kind: "ident", name: nameTok.text, span: nameTok.span };
    let typeArgs: ast.TypeExpr[] = [];
    if (this.at("<") && (ast.isGenericContainer(nameTok.text) || this.isGenericCtorAhead())) {
      typeArgs = this.parseTypeArgs();
    }
    if (!this.at("(")) {
      throw new ParseError("`go` must be followed by a direct function call", this.peek().span);
    }
    const args = this.parseArgs();
    return { kind: "call", callee, args, typeArgs, span: this.span(nameTok) };
  }

  private parseTxn(): ast.TxnStmt {
    const start = this.expect("txn");
    const body = this.parseBlock();
    return { kind: "txn", body, span: this.span(start) };
  }

  private parseRetry(): ast.RetryStmt {
    const start = this.expect("retry");
    return { kind: "retry", span: this.span(start) };
  }

  /** `assert cond` or `assert cond, "message"` — Python's spelling: the condition,
   *  then an optional comma-separated message expression. */
  private parseAssert(): ast.AssertStmt {
    const start = this.expect("assert");
    const cond = this.parseExpr();
    let message: ast.Expr | null = null;
    if (this.accept(",")) message = this.parseExpr();
    return { kind: "assert", cond, message, span: this.span(start) };
  }

  /** `raise e` or `raise e from cause`. `from` is contextual (like `key` and
   *  `builtin`): an expression never continues with a bare identifier, so a `from`
   *  following the raised value is unambiguously the cause clause. */
  private parseRaise(): ast.RaiseStmt {
    const start = this.expect("raise");
    const value = this.parseExpr();
    let cause: ast.Expr | null = null;
    if (this.at("ident") && this.peek().text === "from") {
      this.next();
      cause = this.parseExpr();
    }
    return { kind: "raise", value, cause, span: this.span(start) };
  }

  /** `try { … } catch e { … }` — exactly one `catch`, taking no type: it catches every
   *  error and binds it as `Error`. Selecting on the error's type happens *inside* the
   *  handler (and `raise e` sends back what you don't handle), rather than through a
   *  second dispatch mechanism in the clause list. Both a type annotation and a second
   *  clause are rejected here with a pointed message, since both are things a reader
   *  coming from another language will reach for. */
  private parseTry(): ast.TryStmt {
    const start = this.expect("try");
    const body = this.parseBlock();
    if (!this.at("catch")) {
      throw new ParseError("`try` requires a `catch` clause", this.span(start));
    }
    const cstart = this.next();
    const nameTok = this.expect("ident");
    if (this.at(":")) {
      throw new ParseError(
        "a `catch` clause takes no type: `catch e { … }` catches every error — dispatch on its type inside the handler, and `raise e` to pass on what you don't handle",
        this.peek().span
      );
    }
    const cbody = this.parseBlock();
    if (this.at("catch")) {
      throw new ParseError("a `try` takes exactly one `catch` clause", this.peek().span);
    }
    return {
      kind: "try",
      body,
      handler: { name: nameTok.text, nameSpan: nameTok.span, body: cbody, span: this.span(cstart) },
      span: this.span(start),
    };
  }

  private parseMatch(): ast.MatchStmt {
    const start = this.expect("match");
    const subject = this.parseHeaderExpr();
    this.expect("{");
    const arms: ast.MatchArm[] = [];
    while (!this.at("}") && !this.at("eof")) {
      const astart = this.peek();
      const variantTok = this.expect("ident");
      const bindings: ast.MatchBinding[] = [];
      if (this.accept("(")) {
        while (!this.at(")") && !this.at("eof")) {
          const field = this.expect("ident");
          // `field: name` renames the binding; a bare `field` binds under its
          // own name.
          if (this.accept(":")) {
            const name = this.expect("ident");
            bindings.push({
              field: field.text,
              fieldSpan: field.span,
              name: name.text,
              nameSpan: name.span,
              renamed: true,
            });
          } else {
            bindings.push({
              field: field.text,
              fieldSpan: field.span,
              name: field.text,
              nameSpan: field.span,
              renamed: false,
            });
          }
          if (!this.accept(",")) break;
        }
        this.expect(")");
      }
      let body: ast.Block;
      if (this.accept("=>")) {
        if (this.at("{")) {
          body = this.parseBlock();
        } else {
          const stmt = this.parseStmt();
          body = { kind: "block", stmts: [stmt], span: stmt.span };
        }
      } else {
        body = this.parseBlock();
      }
      this.accept(",");
      arms.push({
        variant: variantTok.text,
        variantSpan: variantTok.span,
        bindings,
        body,
        span: this.span(astart),
        // `_` is a wildcard (catch-all) arm; any other identifier names a variant.
        wildcard: variantTok.text === "_",
      });
    }
    this.expect("}");
    return { kind: "match", subject, arms, span: this.span(start) };
  }

  private parseExprOrAssign(): ast.Stmt {
    const start = this.peek();
    const expr = this.parseExpr();
    if (ASSIGN_OPS.includes(this.peek().type)) {
      const opTok = this.next();
      if (expr.kind !== "ident" && expr.kind !== "field") {
        throw new ParseError("invalid assignment target", expr.span);
      }
      const value = this.parseExpr();
      return {
        kind: "assign",
        target: expr,
        op: opTok.type as ast.AssignStmt["op"],
        value,
        span: this.span(start),
      };
    }
    // Nothing ever follows an expression statement with a `:`, so reaching one means
    // a dict literal was written where a `{` opens a *block* — as a statement, or as
    // a lambda body (`(x) -> {"k": x}`). Say that, rather than "unexpected :".
    if (this.at(":")) {
      throw new ParseError(
        'unexpected `:` after an expression — if you meant a dict literal, a `{` here opens a block: wrap the literal in parentheses, as `({"a": 1})`',
        this.peek().span
      );
    }
    return { kind: "exprStmt", expr, span: this.span(start) };
  }

  // -- expressions --------------------------------------------------------

  private parseExpr(): ast.Expr {
    return this.parseOr();
  }

  private parseOr(): ast.Expr {
    let left = this.parseAnd();
    while (this.at("||")) {
      const start = left;
      this.next();
      const right = this.parseAnd();
      left = { kind: "binary", op: "||", left, right, span: joinSpan(start.span, right.span) };
    }
    return left;
  }

  private parseAnd(): ast.Expr {
    let left = this.parseEquality();
    while (this.at("&&")) {
      const start = left;
      this.next();
      const right = this.parseEquality();
      left = { kind: "binary", op: "&&", left, right, span: joinSpan(start.span, right.span) };
    }
    return left;
  }

  private parseEquality(): ast.Expr {
    let left = this.parseComparison();
    while (this.at("==") || this.at("!=")) {
      const op = this.next().type as "==" | "!=";
      const right = this.parseComparison();
      left = { kind: "binary", op, left, right, span: joinSpan(left.span, right.span) };
    }
    return left;
  }

  private parseComparison(): ast.Expr {
    let left = this.parseRange();
    while (this.at("<") || this.at(">") || this.at("<=") || this.at(">=")) {
      const op = this.next().type as "<" | ">" | "<=" | ">=";
      const right = this.parseRange();
      left = { kind: "binary", op, left, right, span: joinSpan(left.span, right.span) };
    }
    return left;
  }

  private parseRange(): ast.Expr {
    const left = this.parseAdditive();
    if (this.at("..")) {
      const dotdot = this.next();
      const right = this.parseAdditive();
      // `start..end` is pure surface sugar: desugar it here to a construction of
      // the stdlib `Range` struct (which implements `Iterable<int>`). Nothing past
      // the parser knows about ranges — the checker, Core IR, interpreter, and Go
      // backend see an ordinary struct value. The `Range` name resolves to the
      // stdlib type unless the program shadows it with its own `Range`.
      return {
        kind: "call",
        callee: { kind: "ident", name: "Range", span: dotdot.span },
        args: [
          { name: "start", nameSpan: dotdot.span, value: left },
          { name: "end", nameSpan: dotdot.span, value: right },
        ],
        typeArgs: [],
        span: joinSpan(left.span, right.span),
      };
    }
    return left;
  }

  private parseAdditive(): ast.Expr {
    let left = this.parseMultiplicative();
    while (this.at("+") || this.at("-")) {
      const op = this.next().type as "+" | "-";
      const right = this.parseMultiplicative();
      left = { kind: "binary", op, left, right, span: joinSpan(left.span, right.span) };
    }
    return left;
  }

  private parseMultiplicative(): ast.Expr {
    let left = this.parseUnary();
    while (this.at("*") || this.at("/") || this.at("%")) {
      const op = this.next().type as "*" | "/" | "%";
      const right = this.parseUnary();
      left = { kind: "binary", op, left, right, span: joinSpan(left.span, right.span) };
    }
    return left;
  }

  private parseUnary(): ast.Expr {
    if (this.at("-") || this.at("!")) {
      const opTok = this.next();
      const operand = this.parseUnary();
      return {
        kind: "unary",
        op: opTok.type as "-" | "!",
        operand,
        span: joinSpan(opTok.span, operand.span),
      };
    }
    return this.parsePostfix();
  }

  private parsePostfix(): ast.Expr {
    let expr = this.parsePrimary();
    // A generic constructor with explicit type arguments, e.g. `Set<Point>()`,
    // `Dict<int, Point>()`, or a user type `Pair<int, string>(...)` / `None<int>()`.
    // The built-in containers are always recognized; for any other identifier we
    // only commit when the tokens form `<types...>(` (see `isGenericCtorAhead`),
    // keeping `<` unambiguous with the comparison operator.
    if (
      expr.kind === "ident" &&
      this.at("<") &&
      (ast.isGenericContainer(expr.name) || this.isGenericCtorAhead())
    ) {
      const typeArgs = this.parseTypeArgs();
      const args = this.parseArgs();
      expr = {
        kind: "call",
        callee: expr,
        args,
        typeArgs,
        span: joinSpan(expr.span, this.tokens[this.pos - 1]!.span),
      };
    }
    for (;;) {
      if (this.at(".")) {
        this.next();
        const field = this.expect("ident");
        expr = {
          kind: "field",
          object: expr,
          field: field.text,
          fieldSpan: field.span,
          span: joinSpan(expr.span, field.span),
        };
      } else if (this.at("(")) {
        const args = this.parseArgs();
        expr = {
          kind: "call",
          callee: expr,
          args,
          typeArgs: [],
          span: joinSpan(expr.span, this.tokens[this.pos - 1]!.span),
        };
      } else {
        break;
      }
    }
    return expr;
  }

  private parseArgs(): ast.CallArg[] {
    this.expect("(");
    const args = this.parseBracketed(() => {
      const out: ast.CallArg[] = [];
      while (!this.at(")") && !this.at("eof")) {
        // named argument: ident ':' expr
        if (this.at("ident") && this.peek(1).type === ":") {
          const nameTok = this.next();
          this.next(); // ':'
          out.push({ name: nameTok.text, nameSpan: nameTok.span, value: this.parseExpr() });
        } else {
          out.push({ name: null, value: this.parseExpr() });
        }
        if (!this.accept(",")) break;
      }
      return out;
    });
    this.expect(")");
    return args;
  }

  private parsePrimary(): ast.Expr {
    const t = this.peek();
    switch (t.type) {
      case "int":
        this.next();
        return { kind: "int", value: t.value as number, span: t.span };
      case "float":
        this.next();
        return { kind: "float", value: t.value as number, span: t.span };
      case "string":
        this.next();
        return { kind: "string", value: t.value as string, span: t.span };
      case "char":
        this.next();
        return { kind: "char", value: t.value as number, span: t.span };
      case "true":
        this.next();
        return { kind: "bool", value: true, span: t.span };
      case "false":
        this.next();
        return { kind: "bool", value: false, span: t.span };
      case "ident":
        this.next();
        return { kind: "ident", name: t.text, span: t.span };
      case "[": {
        // A list literal `[a, b, c]` — elements are full expressions, and a trailing
        // comma is allowed (like an argument list) so multi-line literals diff
        // cleanly. `[` is unambiguous in expression position: nothing else in the
        // grammar starts with it, and postfix `[` (indexing) does not exist.
        this.next();
        const elements = this.parseBracketed(() => {
          const out: ast.Expr[] = [];
          while (!this.at("]") && !this.at("eof")) {
            out.push(this.parseExpr());
            if (!this.accept(",")) break;
          }
          return out;
        });
        this.expect("]");
        return { kind: "list", elements, span: this.span(t) };
      }
      case "{": {
        // A brace literal — `{k: v, …}` (a dict) or `{a, b, …}` (a set). Unlike `[`,
        // a `{` also opens a block, so in a control-flow header (where a block
        // follows the expression) this is not a literal position, and the collision
        // is reported rather than guessed at.
        if (this.noBraceLiteral) {
          throw new ParseError(
            "a `{ … }` set or dict literal can't appear directly in an `if`/`while`/`for`/`match` header, where `{` opens the body — wrap it in parentheses",
            t.span
          );
        }
        this.next();
        // Which container it is comes from the FIRST element: a `:` after it means a
        // dict, anything else a set. One token of lookahead, no backtracking — and
        // the rest of the entries must match, since a half-and-half literal is a
        // typo, not a form. An empty `{}` fixes nothing, so `container` stays null
        // and the checker resolves it from the expected type.
        let container: "Set" | "Dict" | null = null;
        const entries = this.parseBracketed(() => {
          const out: ast.BraceEntry[] = [];
          while (!this.at("}") && !this.at("eof")) {
            // A dict key is an expression, so `{name: 1}` keys on the variable
            // `name` and `{"name": 1}` on the string — it only *looks* like the
            // named argument `f(name: 1)`.
            const first = this.parseExpr();
            container ??= this.at(":") ? "Dict" : "Set";
            if (container === "Set") {
              if (this.at(":")) {
                throw new ParseError(
                  "unexpected `:` — this is a set literal, since its first element has no `:`; a dict literal needs one on every entry",
                  this.peek().span
                );
              }
              out.push({ value: first });
            } else {
              if (!this.at(":")) {
                throw new ParseError(
                  "expected `:` — this is a dict literal, since its first entry has one; every entry needs a `key: value`",
                  this.peek().span
                );
              }
              this.next(); // ':'
              out.push({ key: first, value: this.parseExpr() });
            }
            if (!this.accept(",")) break;
          }
          return out;
        });
        this.expect("}");
        return { kind: "brace", container, entries, span: this.span(t) };
      }
      case "if":
        // `if` in expression position is an if-*expression* (`if c { a } else { b }`).
        // A statement-position `if` never reaches here — `parseStmt` handles it as an
        // `IfStmt` before falling through to expression parsing — so the keyword is
        // unambiguous: an `if` an expression parser sees always yields a value.
        return this.parseIfExpr();
      case "(": {
        // `(` begins either a grouped expression or a lambda `(params) -> body`.
        // They're told apart by scanning to the matching `)` and checking for a
        // following `->` (which is never an operator in expression position).
        if (this.isLambdaAhead()) return this.parseLambda();
        this.next();
        const expr = this.parseBracketed(() => this.parseExpr());
        this.expect(")");
        return expr;
      }
      default:
        throw new ParseError(
          `unexpected ${t.type === "eof" ? "end of input" : JSON.stringify(t.text)} in expression`,
          t.span
        );
    }
  }

  /** Parse an if-*expression* `if cond { a } else { b }` (see `ast.IfExpr`). The
   *  `else` is mandatory — an expression must have a value when the condition is
   *  false — and an `else if` chain nests (the `else` is another if-expression). */
  private parseIfExpr(): ast.IfExpr {
    const start = this.expect("if");
    const cond = this.parseHeaderExpr();
    const then = this.parseBranchExpr();
    if (!this.accept("else")) {
      throw new ParseError(
        "an `if` used as an expression must have an `else` branch — its value when the condition is false",
        this.span(start)
      );
    }
    const elseBranch = this.at("if") ? this.parseIfExpr() : this.parseBranchExpr();
    return { kind: "ifExpr", cond, then, else: elseBranch, span: this.span(start) };
  }

  /** A branch of an if-expression: a single expression in braces, `{ expr }`. The
   *  braces hold an *expression*, not a statement block — so a `{ … }` set/dict
   *  literal is unambiguous here (a block never appears in this position), and brace
   *  literals are re-enabled inside via `parseBracketed`, as in any bracketed group. */
  private parseBranchExpr(): ast.Expr {
    this.expect("{");
    const e = this.parseBracketed(() => this.parseExpr());
    this.expect("}");
    return e;
  }

  /** With the cursor on a `<`, do the following tokens form a generic
   *  constructor's type-argument list — `<types...>` immediately followed by
   *  `(`? Scans a balanced `<...>` (allowing nested angles and function-type
   *  parens) made only of type-shaped tokens; anything else means it's really a
   *  comparison operator, so we bail. */
  private isGenericCtorAhead(): boolean {
    let angle = 0;
    for (let i = 0; ; i++) {
      const t = this.peek(i);
      switch (t.type) {
        case "<":
          angle++;
          break;
        case ">":
          if (--angle === 0) return this.peek(i + 1).type === "(";
          break;
        case "ident":
        case ",":
        case "(":
        case ")":
        case "->":
          break;
        default:
          return false; // a literal / operator / eof — not a type-argument list
      }
    }
  }

  /** With the cursor on a `(`, is this the parameter list of a lambda? Scans to
   *  the matching close paren (balancing nested parens, which appear in
   *  function-type annotations like `(f: (int) -> int)`) and reports whether an
   *  `->` follows it. */
  private isLambdaAhead(): boolean {
    let depth = 0;
    for (let i = 0; ; i++) {
      const t = this.peek(i);
      if (t.type === "eof") return false;
      if (t.type === "(") depth++;
      else if (t.type === ")" && --depth === 0) return this.peek(i + 1).type === "->";
    }
  }

  /** Parse a lambda `(params) -> body`. Parameters may be untyped (`(x) -> ...`)
   *  or annotated (`(x: int) -> ...`); the body is an expression. */
  private parseLambda(): ast.LambdaExpr {
    const start = this.expect("(");
    const params: ast.LambdaParam[] = [];
    while (!this.at(")") && !this.at("eof")) {
      const pstart = this.peek();
      const name = this.expect("ident");
      let type: ast.TypeExpr | null = null;
      if (this.accept(":")) type = this.parseType();
      params.push({ name: name.text, nameSpan: name.span, type, span: this.span(pstart) });
      if (!this.accept(",")) break;
    }
    this.expect(")");
    this.expect("->");
    // A block body `(x) -> { ... }` runs statements and returns via `return`; an
    // expression body `(x) -> e` returns the expression's value.
    const body = this.at("{") ? this.parseBlock() : this.parseExpr();
    return { kind: "lambda", params, body, span: this.span(start) };
  }
}

function joinSpan(a: Span, b: Span): Span {
  return { start: a.start, end: b.end };
}
